import React, { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { Typography, Box, Skeleton } from '@mui/material'
import { NodeRendererProps, Tree, TreeApi } from 'react-arborist'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

import { Annotation, FileListNodeProp, Marker } from '../../../utils/types'
import { mergePolyList } from './processAnnotations'
import { defaultMarkerColor } from '../../../styles/js/colors.style'
import { ViewerContext } from './context'
import { handleAnnoCheckbox } from './common'


const Node = ({ node, style }: NodeRendererProps<any>) => {
    return (
        <div style={style} className='node-container cursor-pointer' onClick={() => node.toggle()}>
            {node.data.children 
                ? <>
                    {node.isOpen ? <ExpandLess /> : <ExpandMore />}
                    <input
                        type="checkbox"
                        value=''
                        checked={node.data.checked}
                        className="form-check-input m-0"
                        style={{
                            pointerEvents: 'none',
                            backgroundColor: node.data.color,
                            borderColor: node.data.color,
                            outline: 'none'
                        }}
                        readOnly
                    />
                </>
                : null
            }
            <Typography style={{pointerEvents: 'none'}} className='node-text' title={node.data.name.split('/').pop()}>
                {node.data.name.split('/').pop()}
            </Typography>
        </div>
    )
}

interface SysAnnotationsListProps {
    sysMarkers: Marker[]
    userMarkers: Annotation[]
    showByCategory: Function
    zoomToMarker: Function
    sysAnnoLoader: boolean
}

const SysAnnotationsList = ({
    sysMarkers,
    userMarkers,
    showByCategory,
    zoomToMarker,
    sysAnnoLoader
}: SysAnnotationsListProps) => {
    const { sysClassFilter, colorLib } = useContext(ViewerContext)
    
    const treeRef = useRef<TreeApi<FileListNodeProp>>(null)
    
    const [boxHeight, setBoxHeight] = useState<number>(0)
    const [nodeList, setNodeList] = useState<FileListNodeProp[]>([])
    const [selectedNode, setSelectedNode] = useState<FileListNodeProp | null>(null)

    const rowHeight = 32
    const maxHeight: number = (window.visualViewport?.height ?? window.innerHeight) - 240

    const createNodeList = useCallback(async () => {
        const temp: {[x: string]: FileListNodeProp[]} = {}
        const markers = mergePolyList(sysMarkers, userMarkers)
        markers.forEach((i: Marker | Annotation) => {
            if (i.category && i.category.toLowerCase() !== 'negative-area' && !i.is_deleted) {
                if (!(i.category in temp)) temp[i.category] = []
                temp[i.category].push({
                    id: i.id,
                    name: `${i.category}-${temp[i.category].length + 1}`
                })
            }
        })
        const nodes = Object.keys(temp).map(key => {
            const colorIndex: number = colorLib.findIndex(i => i.name.toLowerCase() === key.toLowerCase())
            const color: string = colorIndex !== -1 ? colorLib[colorIndex].color : defaultMarkerColor
            return {
                id: uuidv4(),
                name: key,
                color: color,
                checked: sysClassFilter.includes(key.toLowerCase()),
                children: temp[key]
            }
        })
        setNodeList(nodes)
        setBoxHeight(18 + (nodes.length * rowHeight))
    }, [sysMarkers, userMarkers, colorLib, sysClassFilter])

    useEffect(() => {
        createNodeList()
    }, [sysMarkers, userMarkers])  // eslint-disable-line

    useEffect(() => {
        setNodeList(prevList => {
            if (!prevList.length) return prevList
            return prevList.map(i => {
                const colorIndex: number = colorLib.findIndex(j => j.name.toLowerCase() === i.name.toLowerCase())
                const color: string = colorIndex !== -1 ? colorLib[colorIndex].color : defaultMarkerColor
                return {...i, color: color}
            })
        })
    }, [colorLib])

    useEffect(() => {
        setNodeList(prevList => {
            if (!prevList.length) return prevList
            prevList.forEach(i => sysClassFilter.includes(i.name.toLowerCase()) ? treeRef.current?.open(i.id) : treeRef.current?.close(i.id))
            return prevList.map(i => {
                return {...i, checked: sysClassFilter.includes(i.name.toLowerCase())}
            })
        })
    }, [sysClassFilter])  // eslint-disable-line

    const updateBoxHeight = (list: FileListNodeProp[]) => {
        try {
            let newHeight = 18
            for (let i = 0; i < list.length; i++) {
                newHeight += rowHeight
                const item = list[i]
                if (item.checked && item.children) {
                    newHeight += item.children.length * rowHeight
                    const node = treeRef.current?.get(item.id)
                    if (!(node?.isOpen ?? false)) treeRef.current?.open(item.id)
                }
            }
            if (newHeight > maxHeight) setBoxHeight(maxHeight)
            else setBoxHeight(newHeight)
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        updateBoxHeight(nodeList)
    }, [nodeList])  // eslint-disable-line

    const onSelect = (nodes: any[]) => {
        if (nodes.length) {
            try {
                handleAnnoCheckbox('')
                if (nodes[0].isLeaf) {
                    if (selectedNode && nodes[0].data.id!==selectedNode.id) zoomToMarker(selectedNode.id, true)
                    zoomToMarker(nodes[0].data.id, false)
                } else {
                    showByCategory(nodes[0].data.name.toLowerCase())
                }
                setSelectedNode(nodes[0].data)
            } catch (err) {
                console.log(err)
            }
        }
    }

    return (
        <Box sx={{width: '100%', maxHeight: 'calc(100vh - 240px)', overflow: 'auto'}}>
            {sysAnnoLoader
                ? <Skeleton variant="rounded" height={34} />
                : nodeList.length
                    ? <Tree
                        ref={treeRef}
                        data={nodeList}
                        openByDefault={false}
                        rowHeight={rowHeight}
                        onSelect={onSelect}
                        height={boxHeight}
                        width='auto'
                        indent={49}
                        disableDrag={true}
                    >
                        {Node}
                    </Tree>
                    : <Typography className='text-secondary'>No annotations</Typography>
            }
        </Box>
    )
}

export default SysAnnotationsList